import { useState, useCallback, useRef } from 'react';

// API base URL - empty string in production (same origin), localhost in dev
const API_BASE = import.meta.env.PROD ? '' : 'http://localhost:3001';

/**
 * Hook for interacting with the Touchstone AI backend
 */
export function useAI() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const abortControllerRef = useRef(null);

  /**
   * Check AI service status
   */
  const checkStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/ai/status`);
      const data = await response.json();
      setStatus(data);
      return data;
    } catch (err) {
      setStatus({ status: 'offline', error: err.message });
      return null;
    }
  }, []);

  /**
   * Send a chat message and get a streaming response
   * @param {Object} options
   * @param {Array} options.messages - Chat history
   * @param {Object} options.context - Current app context
   * @param {Function} options.onChunk - Callback for each streamed chunk
   * @param {Function} options.onComplete - Callback when complete
   * @param {Function} options.onError - Error callback
   */
  const sendMessage = useCallback(async ({ 
    messages, 
    context = {}, 
    onChunk, 
    onComplete, 
    onError 
  }) => {
    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages, context, stream: true }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send message');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') {
              onComplete?.(fullContent);
              break;
            }
            try {
              const parsed = JSON.parse(data);
              if (parsed.content) {
                fullContent += parsed.content;
                onChunk?.(parsed.content, fullContent);
              }
              if (parsed.error) {
                throw new Error(parsed.error);
              }
            } catch (parseError) {
              // Ignore parse errors for incomplete chunks
            }
          }
        }
      }

      setIsLoading(false);
      return fullContent;
    } catch (err) {
      if (err.name === 'AbortError') {
        setIsLoading(false);
        return null;
      }
      setError(err.message);
      setIsLoading(false);
      onError?.(err);
      throw err;
    }
  }, []);

  /**
   * Send a non-streaming chat message
   */
  const sendMessageSync = useCallback(async ({ messages, context = {} }) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages, context, stream: false }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send message');
      }

      const data = await response.json();
      setIsLoading(false);
      return data.content;
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
      throw err;
    }
  }, []);

  /**
   * Get suggested questions based on context
   */
  const getSuggestions = useCallback(async (context = {}) => {
    try {
      const response = await fetch(`${API_BASE}/api/ai/suggest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ context }),
      });

      if (!response.ok) {
        return [];
      }

      const data = await response.json();
      return data.suggestions || [];
    } catch (err) {
      return [];
    }
  }, []);

  /**
   * Conversational guide - asks questions and recommends tools
   * @param {Array} messages - Conversation history
   * @param {Object} context - User context
   * @returns {Promise<Object>} - {message, highlightTools, dimTools, stage}
   */
  const guide = useCallback(async (messages, context = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/ai/guide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages, context }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Guide request failed');
      }

      const data = await response.json();
      setIsLoading(false);
      return data;
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
      return {
        message: "Sorry, I'm having trouble. Try again?",
        highlightTools: [],
        dimTools: [],
        stage: 'error'
      };
    }
  }, []);

  /**
   * Semantic search for tools
   * @param {string} query - Natural language search query
   * @param {Object} context - User context
   * @returns {Promise<string[]>} - Array of tool names
   */
  const semanticSearch = useCallback(async (query, context = {}) => {
    if (!query?.trim()) return [];
    
    try {
      const response = await fetch(`${API_BASE}/api/ai/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, context, limit: 8 }),
      });

      if (!response.ok) {
        return [];
      }

      const data = await response.json();
      return data.results || [];
    } catch (err) {
      console.error('Semantic search error:', err);
      return [];
    }
  }, []);

  /**
   * Cancel the current request
   */
  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsLoading(false);
  }, []);

  return {
    sendMessage,
    sendMessageSync,
    getSuggestions,
    semanticSearch,
    guide,
    checkStatus,
    cancel,
    isLoading,
    error,
    status,
    isOnline: status?.status === 'ready',
  };
}

export default useAI;
